// Tab "Quản lý liệu trình": toàn bộ thẻ của khách, tiến độ từng dịch vụ, lịch thanh toán, buổi đã làm kèm chữ ký + ảnh, thao tác nhanh
import { useState } from 'react';
import { Segmented, Tooltip } from 'antd';
import dayjs from 'dayjs';
import {
  CalendarPlus, Camera, ChevronDown, ChevronRight, CircleDollarSign, ExternalLink, FileCheck2, FileSignature, ScanLine, TicketPlus,
} from 'lucide-react';
import { staffName } from '../POS/data';
import type { TreatmentCard } from '../Treatment/data';
import { cardBalance, cardFlags, expiryText, installmentRows, lineBalances, outstanding, paidAmount } from '../Treatment/logic';
import { useTreatmentAuth } from '../Treatment/actions';
import { pendingRequestOf } from '../Treatment/PayRequestModal';
import useTreatmentStore from '../Treatment/store';
import { BalanceCell, CARD_STATUS, FlagPills, INSTALLMENT_STATE, Kpi, Pill, fmtDate, money, moneyShort } from '../Treatment/ui';
import { Btn, LinkBar, Section, type C360Ops } from './ui360';
import type { C360 } from './useCustomer360';

type Filter = 'active' | 'all';

export default function TreatmentPanel({ c, ops, onViewDoc }: { c: C360; ops: C360Ops; onViewDoc: (docId: string) => void }) {
  const cfg = useTreatmentStore(s => s.config);
  const requests = useTreatmentStore(s => s.payRequests);
  const { can } = useTreatmentAuth();
  const [filter, setFilter] = useState<Filter>('active');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const cards = c.data.cards
    .map(card => ({ card, f: cardFlags(card, cfg) }))
    .filter(x => filter === 'all' || x.f.status === 'active')
    .sort((a, b) => b.card.createdAt.localeCompare(a.card.createdAt));
  const m = c.metrics;
  const totalPrice = c.data.cards.filter(x => x.status !== 'cancelled').reduce((s, x) => s + x.price, 0);
  const totalPaid = c.data.cards.reduce((s, x) => s + paidAmount(x), 0);

  return (
    <div className="space-y-4">
      <LinkBar c={c} />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Thẻ đang dùng" value={`${m.activeCards}/${c.data.cards.length}`} sub={`Đã làm ${m.usedSessions} buổi`} />
        <Kpi label="Buổi còn lại" value={m.sessionsLeft} sub={m.valueLeft ? `+ ${moneyShort(m.valueLeft)} thẻ trả trước` : 'Thẻ buổi'} />
        <Kpi label="Đã thanh toán" value={moneyShort(totalPaid)} sub={`/ ${moneyShort(totalPrice)} giá trị thẻ`} />
        <Kpi label="Còn nợ thẻ" value={`${money(c.data.cards.reduce((s, x) => s + outstanding(x), 0))}đ`} tone={m.overdue ? 'danger' : 'default'} sub={m.overdue ? `Quá hạn ${money(m.overdue)}đ` : ' '} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Segmented value={filter} onChange={v => setFilter(v as Filter)} options={[{ value: 'active', label: 'Đang dùng' }, { value: 'all', label: 'Tất cả thẻ' }]} />
        {can('create') && <Btn primary onClick={ops.sell}><TicketPlus className="h-4 w-4" /> Bán thẻ liệu trình</Btn>}
      </div>

      {!cards.length && <div className="rounded-xl border border-dashed border-slate-300 py-10 text-center text-sm text-slate-400">{filter === 'active' ? 'Khách không có thẻ đang dùng' : 'Khách chưa có thẻ liệu trình'}</div>}

      {cards.map(({ card, f }) => (
        <CardBlock key={card.id} c={c} card={card} f={f} ops={ops} expanded={!!open[card.id]} toggle={() => setOpen(o => ({ ...o, [card.id]: !o[card.id] }))}
          pending={!!pendingRequestOf(requests, card.id)} onViewDoc={onViewDoc} />
      ))}
    </div>
  );
}

function CardBlock({ c, card, f, ops, expanded, toggle, pending, onViewDoc }: {
  c: C360; card: TreatmentCard; f: ReturnType<typeof cardFlags>; ops: C360Ops; expanded: boolean; toggle: () => void; pending: boolean; onViewDoc: (id: string) => void;
}) {
  const cfg = useTreatmentStore(s => s.config);
  const { can } = useTreatmentAuth();
  const lines = lineBalances(card);
  const rows = installmentRows(card, cfg);
  const next = rows.find(r => r.left > 0 && r.state !== 'void');
  const b = cardBalance(card);
  const docs = c.data.docs.filter(d => d.cardId === card.id && d.status === 'signed');
  const contract = docs.find(d => d.kind === 'contract');
  const photos = c.data.photos.filter(p => p.cardId === card.id && !p.hidden);
  const usages = card.usages.slice().sort((a, b2) => b2.at.localeCompare(a.at));
  const upcoming = card.bookings.filter(x => x.status === 'booked' && x.at >= new Date().toISOString()).sort((a, b2) => a.at.localeCompare(b2.at));
  const live = f.status === 'active';

  return (
    <Section
      title={
        <span className="flex flex-wrap items-center gap-2">
          {card.packageName}
          <span className="font-mono text-xs font-normal text-slate-400">{card.id}</span>
          <Pill {...CARD_STATUS[f.status]} />
          <FlagPills f={f} compact />
          {contract
            ? <Tooltip title={`Đã ký ${contract.id}`}><button onClick={() => onViewDoc(contract.id)} className="inline-flex items-center gap-0.5 text-xs font-medium text-green-700"><FileCheck2 className="h-3.5 w-3.5" /> Đã ký HĐ</button></Tooltip>
            : card.status !== 'cancelled' && <button onClick={() => ops.sign({ kind: 'contract', cardId: card.id })} className="inline-flex items-center gap-0.5 text-xs font-medium text-amber-700 hover:underline"><FileSignature className="h-3.5 w-3.5" /> Chưa ký HĐ</button>}
        </span>
      }
      extra={<>
        {live && can('create') && <Btn primary onClick={() => ops.deduct(card.id)} disabled={b.pctLeft <= 0}><ScanLine className="h-3.5 w-3.5" /> Trừ buổi</Btn>}
        {live && can('create') && <Btn onClick={() => ops.book(card.id)}><CalendarPlus className="h-3.5 w-3.5" /> Đặt lịch</Btn>}
        {can('create') && (f.debt > 0 || pending) && <Btn onClick={() => ops.pay(card.id)}><CircleDollarSign className="h-3.5 w-3.5" /> {pending ? 'Chờ POS thu…' : 'Gửi POS thu'}</Btn>}
        <Btn onClick={() => ops.openCard(card.id)} title="Mở chi tiết thẻ"><ExternalLink className="h-3.5 w-3.5" /></Btn>
      </>}
    >
      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-2 md:col-span-2">
          <BalanceCell card={card} wide />
          {card.kind === 'session' && (
            <div className="grid gap-1.5 sm:grid-cols-2">
              {lines.map(l => {
                const total = l.total + l.bonus - l.refunded;
                return (
                  <div key={l.serviceId} className="rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs">
                    <div className="flex justify-between gap-2"><span className="truncate font-medium text-slate-700">{l.name}{l.role === 'bonus' && <span className="text-green-600"> (tặng)</span>}</span><span className="shrink-0">{l.used}/{total}</span></div>
                    <div className="mt-1 flex gap-0.5">{Array.from({ length: Math.min(total, 24) }).map((_, i) => <span key={i} className={`h-1.5 flex-1 rounded-sm ${i < l.used ? 'bg-[#3e91ab]' : i < l.used + l.booked ? 'bg-amber-300' : 'bg-slate-200'}`} />)}</div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="text-xs text-slate-500">
            Mua {fmtDate(card.createdAt)} · hạn {expiryText(card)}{card.extendedMonths ? ` (đã gia hạn ${card.extendedMonths} tháng)` : ''} · tư vấn {card.consultant || '—'}
            {upcoming[0] && <> · <span className="font-medium text-[#2c6b7f]">Lịch hẹn {dayjs(upcoming[0].at).format('HH:mm DD/MM')} – {upcoming[0].name}</span></>}
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 p-2.5 text-xs">
          <div className="mb-1 flex justify-between font-semibold text-slate-700"><span>Thanh toán</span><span>{money(paidAmount(card))}/{money(card.price)}đ</span></div>
          {rows.map(r => (
            <div key={r.no} className="flex items-center justify-between gap-1 py-0.5">
              <span>Đợt {r.no} · {fmtDate(r.dueDate)}</span>
              <span className="flex items-center gap-1">{money(r.amount)}<Pill {...INSTALLMENT_STATE[r.state]} /></span>
            </div>
          ))}
          {next && <div className="mt-1 text-slate-500">Cần thu tiếp: <b className={next.state === 'overdue' ? 'text-red-600' : 'text-slate-700'}>{money(next.left)}đ</b></div>}
        </div>
      </div>

      <button onClick={toggle} className="mt-3 flex items-center gap-1 text-xs font-semibold text-[#2c6b7f] hover:underline">
        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />} Buổi đã làm ({usages.length}) · Ảnh ({photos.length}) · Văn bản đã ký ({docs.length})
      </button>
      {expanded && (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead><tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-400">
              <th className="py-1.5">Buổi</th><th>Thời gian</th><th>Dịch vụ</th><th>KTV</th><th>Chữ ký</th><th>Ảnh</th>
            </tr></thead>
            <tbody>
              {usages.map((u, i) => {
                // Chữ ký của buổi nằm trong phiếu xác nhận lần đến (hoặc chữ ký từng buổi bản cũ)
                const doc = c.data.docs.find(d => d.status === 'signed' && ((d.kind === 'session' && d.usageId === u.id) || (d.kind === 'visit' && !!d.coveredKeys?.includes(`u:${u.id}`))));
                const paper = u.signed || c.visits.some(v => v.paperKeys.includes(`u:${u.id}`));
                const uPhotos = photos.filter(p => p.usageId === u.id);
                return (
                  <tr key={u.id} className={`border-b border-slate-100 ${u.cancelled ? 'text-slate-400 line-through' : ''}`}>
                    <td className="py-1.5">#{usages.length - i}</td>
                    <td className="whitespace-nowrap">{dayjs(u.at).format('DD/MM/YYYY HH:mm')}</td>
                    <td>{u.items.map(it => `${it.name} ×${it.qty}`).join(', ')}{u.note && <div className="text-xs text-slate-400 no-underline">{u.note}</div>}</td>
                    <td>{staffName(u.staffId) || u.staffId}</td>
                    <td>
                      {u.cancelled ? <span className="text-xs">Đã hủy</span>
                        : doc ? <button onClick={() => onViewDoc(doc.id)} className="text-xs font-semibold text-green-700 hover:underline">Đã ký ({doc.id})</button>
                          : paper ? <span className="text-xs text-slate-500">Ký giấy</span>
                            : <button onClick={() => ops.signVisit(dayjs(u.at).format('YYYY-MM-DD'))} className="text-xs font-semibold text-amber-700 hover:underline">Chưa ký – ký ngay</button>}
                    </td>
                    <td>
                      <button onClick={() => ops.upload({ cardId: card.id, usageId: u.id })} className="inline-flex items-center gap-1 text-xs text-[#2c6b7f] hover:underline">
                        <Camera className="h-3.5 w-3.5" /> {uPhotos.length || '+'}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!usages.length && <tr><td colSpan={6} className="py-4 text-center text-xs text-slate-400">Chưa làm buổi nào</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}
